import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import axios from "axios";
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, RadarChart, PolarGrid, PolarAngleAxis, Radar,
} from "recharts";
import {
  ChevronLeft, ChevronRight, Printer, X, Shield, Sparkles, AlertTriangle, CheckCircle, Users, Building2, Calendar, BarChart3, Lock,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POSITIVE_DIMENSIONS = ["Relações Sociais e Liderança", "Autonomia", "Sentido do Trabalho"];
const isPositive = (n) => POSITIVE_DIMENSIONS.includes(n);

const LEVEL_COLOR = {
  Baixo: "#10B981",
  Moderado: "#CA8A04",
  Alto: "#F97316",
  Crítico: "#EF4444",
};

// Approximate numeric value used purely for chart positioning when in public/safe mode
const LEVEL_TO_CHART_VALUE = {
  Baixo: 1.5,
  Moderado: 2.5,
  Alto: 3.5,
  Crítico: 4.5,
};

const getRiskLevel = (effective) => {
  // Faixas oficiais (idênticas à Seção 4 do laudo NR-01):
  //   BAIXO 1,0–2,0 · MODERADO 2,1–3,0 · ALTO 3,1–4,0 · CRÍTICO 4,1–5,0
  // Arredondamos para 1 casa decimal para que 3.02 caia em MODERADO.
  const score = Math.round((Number(effective) + Number.EPSILON) * 10) / 10;
  if (score <= 2.0) return { level: "Baixo", color: "#10B981" };
  if (score <= 3.0) return { level: "Moderado", color: "#CA8A04" };
  if (score <= 4.0) return { level: "Alto", color: "#F97316" };
  return { level: "Crítico", color: "#EF4444" };
};

const Presentation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token: publicToken } = useParams();
  const publicMode = Boolean(publicToken);

  const empresa = searchParams.get("empresa") || "";
  const setor = searchParams.get("setor") || "";
  const funcao = searchParams.get("funcao") || "";

  const [stats, setStats] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [slideIdx, setSlideIdx] = useState(0);
  const [publicScope, setPublicScope] = useState({ empresa: "", setor: "", funcao: "" });

  const fetchData = useCallback(async () => {
    if (publicMode) {
      // PUBLIC MODE — fetch from public endpoint, no auth required
      try {
        setLoading(true);
        const res = await axios.get(`${API}/public/presentation/${publicToken}`);
        const data = res.data;
        setPublicScope(data.scope || {});
        // Build a stats-like object using qualitative levels
        const dimLevels = data.stats_safe?.dimension_levels || {};
        const safeStats = {
          // numeric fields intentionally omitted
          total_assessments: data.stats_safe?.total_assessments || 0,
          numero_funcionarios: data.stats_safe?.numero_funcionarios || 0,
          sector_counts: data.stats_safe?.sector_counts || {},
          first_assessment_date: data.stats_safe?.first_assessment_date,
          last_assessment_date: data.stats_safe?.last_assessment_date,
          company_info: data.stats_safe?.company_info,
          sector_comparison: data.stats_safe?.sector_comparison || [],
          // synthetic dimension_averages to drive charts (no real numeric values shown anywhere)
          dimension_averages: Object.fromEntries(
            Object.entries(dimLevels).map(([name, info]) => {
              // Reverse the effective→raw transform so the chart logic still works
              const eff = LEVEL_TO_CHART_VALUE[info.level] || 2.5;
              const raw = info.positive ? 6 - eff : eff;
              return [name, raw];
            })
          ),
          overall_level: data.stats_safe?.overall_level || "Moderado",
          dimension_levels: dimLevels,
        };
        setStats(safeStats);
        if (data.ai_analysis) setAiAnalysis(data.ai_analysis);
      } catch (err) {
        console.error(err);
        if (err.response?.status === 410) {
          setError(err.response.data?.detail || "Este link expirou ou foi revogado.");
        } else if (err.response?.status === 404) {
          setError("Link inválido ou inexistente.");
        } else {
          setError("Erro ao carregar a apresentação.");
        }
      } finally {
        setLoading(false);
      }
      return;
    }

    // ADMIN MODE — original behaviour
    if (!empresa) {
      setError("Selecione uma empresa no Dashboard para gerar a apresentação.");
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const tokenJwt = localStorage.getItem("admin_token");
      const auth = { headers: { Authorization: `Bearer ${tokenJwt}` } };

      const qs = new URLSearchParams({ empresa });
      if (setor) qs.append("setor", setor);
      if (funcao) qs.append("funcao", funcao);

      const statsRes = await axios.get(`${API}/assessments/stats?${qs.toString()}`, auth);
      setStats(statsRes.data);

      // Try to fetch the latest AI analysis for this scope (optional)
      try {
        const aiQs = new URLSearchParams({ empresa, limit: 1 });
        if (setor) aiQs.append("setor", setor);
        if (funcao) aiQs.append("funcao", funcao);
        const aiHist = await axios.get(`${API}/ai/analyses?${aiQs.toString()}`, auth);
        if (Array.isArray(aiHist.data) && aiHist.data.length > 0) {
          setAiAnalysis(aiHist.data[0]);
        }
      } catch (_) {
        // AI analysis is optional
      }
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/login");
      } else {
        setError("Erro ao carregar dados da apresentação.");
      }
    } finally {
      setLoading(false);
    }
  }, [empresa, setor, funcao, navigate, publicMode, publicToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Effective company/scope for rendering (works for both modes)
  const effEmpresa = publicMode ? publicScope.empresa : empresa;
  const effSetor = publicMode ? publicScope.setor : setor;

  // Derived data
  const chartData = useMemo(() => {
    if (!stats || !stats.dimension_averages) return [];
    return Object.entries(stats.dimension_averages).map(([name, value]) => {
      const v = parseFloat(value.toFixed(2));
      const effective = isPositive(name) ? 6 - v : v;
      const risk = getRiskLevel(effective);
      return { name, value: v, effective, color: risk.color, level: risk.level, positive: isPositive(name) };
    });
  }, [stats]);

  const gravityDistribution = useMemo(() => {
    if (chartData.length === 0) return [];
    return [
      { name: "Baixo", value: chartData.filter((d) => d.level === "Baixo").length, color: "#10B981" },
      { name: "Moderado", value: chartData.filter((d) => d.level === "Moderado").length, color: "#CA8A04" },
      { name: "Alto", value: chartData.filter((d) => d.level === "Alto").length, color: "#F97316" },
      { name: "Crítico", value: chartData.filter((d) => d.level === "Crítico").length, color: "#EF4444" },
    ].filter((d) => d.value > 0);
  }, [chartData]);

  const topCritical = useMemo(
    () => [...chartData].sort((a, b) => b.effective - a.effective).slice(0, 3),
    [chartData]
  );

  const overallRisk = useMemo(() => {
    if (publicMode) {
      const lvl = stats?.overall_level || "Moderado";
      return { level: lvl, color: LEVEL_COLOR[lvl] || "#CA8A04" };
    }
    return getRiskLevel(stats?.overall_score || 0);
  }, [stats, publicMode]);

  const adesaoPercent = useMemo(() => {
    if (!stats?.numero_funcionarios || stats.numero_funcionarios === 0) return null;
    return Math.round(((stats?.total_assessments || 0) / stats.numero_funcionarios) * 100);
  }, [stats]);

  const isMultisetorial = !effSetor;
  const sectorComparison = stats?.sector_comparison || [];

  // Build slides list (skip sector comparison if not multisetorial)
  const slides = useMemo(() => {
    const list = [
      { id: "cover", title: "Capa" },
      { id: "about", title: "Sobre a Avaliação" },
      { id: "big-numbers", title: "Indicadores Principais" },
      { id: "radar", title: "Panorama Geral" },
      { id: "distribution", title: "Distribuição de Riscos" },
      { id: "dimensions", title: "Análise por Dimensão" },
    ];
    if (isMultisetorial && sectorComparison.length > 1) {
      list.push({ id: "sectors", title: "Comparativo Setorial" });
    }
    list.push({ id: "critical", title: "Pontos Críticos & Intervenção" });
    list.push({ id: "conclusion", title: "Conclusão" });
    list.push({ id: "closing", title: "Encerramento" });
    return list;
  }, [isMultisetorial, sectorComparison.length]);

  const totalSlides = slides.length;

  const goNext = useCallback(() => setSlideIdx((i) => Math.min(i + 1, totalSlides - 1)), [totalSlides]);
  const goPrev = useCallback(() => setSlideIdx((i) => Math.max(i - 1, 0)), []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") {
        e.preventDefault();
        goNext();
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "Home") {
        e.preventDefault();
        setSlideIdx(0);
      } else if (e.key === "End") {
        e.preventDefault();
        setSlideIdx(totalSlides - 1);
      } else if (e.key === "Escape") {
        if (!publicMode) navigate("/dashboard");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goNext, goPrev, totalSlides, navigate, publicMode]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Preparando apresentação...</p>
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <AlertTriangle className="w-12 h-12 text-orange-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Não foi possível gerar a apresentação</h2>
          <p className="text-gray-600 mb-6">{error || "Dados insuficientes."}</p>
          <button
            onClick={() => navigate("/dashboard")}
            className="px-5 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-semibold"
          >
            Voltar ao Dashboard
          </button>
        </div>
      </div>
    );
  }

  const current = slides[slideIdx];
  const scopeLabel = effSetor ? `Setor: ${effSetor}` : "Visão Multisetorial — Consolidado da Empresa";
  const todayStr = new Date().toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
  const numSectors = stats?.sector_counts ? Object.keys(stats.sector_counts).length : 0;

  return (
    <div className="min-h-screen bg-slate-100 presentation-root" data-testid="presentation-root">
      {/* Top toolbar — hidden in print */}
      <header className="bg-white border-b border-gray-200 px-3 sm:px-5 py-2.5 sm:py-3 flex items-center justify-between sticky top-0 z-30 print:hidden gap-2">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          {!publicMode && (
            <button
              onClick={() => navigate("/dashboard")}
              className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              data-testid="presentation-close-btn"
              title="Fechar (Esc)"
            >
              <X className="w-5 h-5" />
            </button>
          )}
          <div className="min-w-0 flex-1">
            <h1 className="text-xs sm:text-sm font-bold text-gray-900 flex items-center gap-1.5 sm:gap-2 truncate">
              <span className="truncate">{effEmpresa}</span>
              {publicMode && (
                <span className="inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 bg-orange-100 text-orange-700 text-[9px] sm:text-[10px] font-bold rounded-full flex-shrink-0" data-testid="public-mode-badge">
                  <Lock className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                  PÚBLICO
                </span>
              )}
            </h1>
            <p className="text-[10px] sm:text-xs text-gray-500 truncate">{scopeLabel}</p>
          </div>
        </div>
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          <span className="text-[10px] sm:text-xs text-gray-500 font-mono">
            {slideIdx + 1}/{totalSlides}
          </span>
          <button
            onClick={goPrev}
            disabled={slideIdx === 0}
            className="p-1.5 sm:p-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="presentation-prev-btn"
            title="Anterior (←)"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={goNext}
            disabled={slideIdx === totalSlides - 1}
            className="p-1.5 sm:p-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="presentation-next-btn"
            title="Próximo (→)"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all text-xs font-semibold"
            data-testid="presentation-print-btn"
            title="Imprimir / Salvar como PDF (Ctrl+P)"
          >
            <Printer className="w-4 h-4" />
            <span className="hidden sm:inline">Imprimir / PDF</span>
          </button>
        </div>
      </header>

      {/* Slide canvas — when screen: only show current; when print: show all */}
      <main className="px-2 sm:px-4 py-2 sm:py-4 print:p-0">
        {slides.map((s, idx) => {
          const isCurrent = idx === slideIdx;
          return (
            <section
              key={s.id}
              data-testid={`slide-${s.id}`}
              className={`presentation-slide bg-white rounded-2xl shadow-lg mx-auto mb-4 overflow-hidden ${
                isCurrent ? "flex flex-col" : "hidden"
              } print:!flex print:flex-col print:shadow-none print:rounded-none print:mb-0 print:mx-0`}
              style={{ maxWidth: 1100 }}
            >
              <SlideContent
                slideId={s.id}
                empresa={effEmpresa}
                scopeLabel={scopeLabel}
                todayStr={todayStr}
                stats={stats}
                chartData={chartData}
                gravityDistribution={gravityDistribution}
                topCritical={topCritical}
                overallRisk={overallRisk}
                adesaoPercent={adesaoPercent}
                numSectors={numSectors}
                sectorComparison={sectorComparison}
                aiAnalysis={aiAnalysis}
                slideNumber={idx + 1}
                totalSlides={totalSlides}
                slideTitle={s.title}
                publicMode={publicMode}
              />
            </section>
          );
        })}
      </main>

      {/* Bottom dot indicator — hidden in print */}
      <footer className="fixed bottom-2 left-1/2 -translate-x-1/2 bg-white shadow-md px-3 py-1.5 rounded-full flex items-center gap-1.5 z-30 print:hidden">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setSlideIdx(i)}
            className={`transition-all rounded-full ${
              i === slideIdx ? "bg-orange-500 w-6 h-2" : "bg-gray-300 hover:bg-gray-400 w-2 h-2"
            }`}
            aria-label={`Ir para slide ${i + 1}`}
          />
        ))}
      </footer>

      <style>{`
        /* Every slide must FIT in the viewport — no scroll up/down inside the slide.
           Toolbar ≈ 50px + dot footer ≈ 30px + vertical padding ≈ 24px ≈ 110px reserved. */
        .presentation-slide {
          height: calc(100dvh - 110px);
          max-height: calc(100dvh - 110px);
          width: 100%;
        }
        /* Tablet and up: cap with 16:9 aspect ratio so the slide stays presentable. */
        @media (min-width: 768px) {
          .presentation-slide {
            height: min(calc(100dvh - 120px), calc((100vw - 32px) * 9 / 16));
            max-height: calc(100dvh - 120px);
            aspect-ratio: 16 / 9;
          }
        }
        @media print {
          @page { size: A4 landscape; margin: 0; }
          html, body { background: white !important; }
          .presentation-slide {
            page-break-after: always;
            break-after: page;
            width: 100% !important;
            height: 100vh !important;
            max-height: 100vh !important;
            aspect-ratio: auto !important;
            border-radius: 0 !important;
          }
          .presentation-slide:last-child {
            page-break-after: auto;
          }
        }
      `}</style>
    </div>
  );
};

// ---------------- Slide content ----------------

const SlideShell = ({ slideTitle, slideNumber, totalSlides, empresa, children, accent = "orange" }) => (
  <div className="w-full h-full flex flex-col p-4 sm:p-6 md:p-8 print:p-8 min-h-0">
    <div className="flex justify-between items-center mb-2 sm:mb-3 pb-2 border-b-2 border-gray-100 gap-3 flex-shrink-0">
      <div className="flex items-center gap-2 min-w-0">
        <div className={`w-1 h-5 sm:h-6 rounded flex-shrink-0 ${accent === "purple" ? "bg-purple-500" : "bg-orange-500"}`}></div>
        <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] sm:tracking-[0.2em] font-bold text-gray-500 truncate">{slideTitle}</span>
      </div>
      <div className="text-[10px] text-gray-400 font-mono whitespace-nowrap flex-shrink-0">
        {slideNumber}/{totalSlides}
      </div>
    </div>
    <div className="flex-1 min-h-0 overflow-hidden flex flex-col">{children}</div>
  </div>
);

const SlideContent = (props) => {
  const { slideId } = props;
  switch (slideId) {
    case "cover":
      return <CoverSlide {...props} />;
    case "about":
      return <AboutSlide {...props} />;
    case "big-numbers":
      return <BigNumbersSlide {...props} />;
    case "radar":
      return <RadarSlide {...props} />;
    case "distribution":
      return <DistributionSlide {...props} />;
    case "dimensions":
      return <DimensionsSlide {...props} />;
    case "sectors":
      return <SectorsSlide {...props} />;
    case "critical":
      return <CriticalSlide {...props} />;
    case "conclusion":
      return <ConclusionSlide {...props} />;
    case "closing":
      return <ClosingSlide {...props} />;
    default:
      return null;
  }
};

// ---------------- Individual slides ----------------

const CoverSlide = ({ empresa, scopeLabel, todayStr, stats }) => {
  const clientLogo = stats?.company_info?.logo_base64;
  return (
    <div
      className="w-full h-full flex flex-col items-center justify-center p-6 sm:p-10 md:p-12 text-center relative"
      style={{
        background: "linear-gradient(135deg, #fff7ed 0%, #ffffff 50%, #fef3c7 100%)",
      }}
    >
      {/* Co-branding logos — stacked on mobile, absolute on tablet+ */}
      <div className="mb-6 sm:mb-0 sm:absolute sm:top-6 sm:right-6 md:top-8 md:right-8 flex items-center gap-3 sm:gap-5">
        {clientLogo && (
          <img src={clientLogo} alt="Logo cliente" className="h-10 sm:h-14 md:h-16 max-w-[100px] sm:max-w-[140px] object-contain" data-testid="cover-client-logo" />
        )}
        <img
          src="/viaseg-logo-print.jpeg"
          alt="Viaseg"
          className="h-10 sm:h-14 md:h-16 object-contain"
          style={{ mixBlendMode: "multiply" }}
        />
      </div>

      <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-xs font-semibold mb-4 sm:mb-6">
        <Shield className="w-3 h-3" /> Conformidade NR-01
      </div>

      <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-3">
        Avaliação de Riscos
        <br />
        <span className="bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent">
          Psicossociais
        </span>
      </h1>
      <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-10 max-w-2xl px-2">
        Apresentação executiva consolidada dos resultados obtidos com o
        questionário COPSOQ II (versão curta).
      </p>

      <div className="bg-white/70 backdrop-blur-sm border border-orange-200 rounded-xl px-4 sm:px-8 py-4 sm:py-5 shadow-sm max-w-full">
        <p className="text-[10px] sm:text-xs uppercase tracking-wide text-gray-500 font-semibold">Empresa avaliada</p>
        <p className="text-lg sm:text-2xl font-bold text-gray-900 mt-1 break-words">{empresa}</p>
        {stats?.company_info?.cnpj && (
          <p className="text-[10px] sm:text-xs text-gray-500 mt-1 font-mono">CNPJ: {stats.company_info.cnpj}</p>
        )}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-6 mt-3 text-[10px] sm:text-xs text-gray-600">
          <span className="inline-flex items-center gap-1">
            <Calendar className="w-3 h-3" /> {todayStr}
          </span>
          <span className="hidden sm:inline">·</span>
          <span>{scopeLabel}</span>
        </div>
      </div>

      <p className="mt-6 sm:mt-0 sm:absolute sm:bottom-6 text-[9px] sm:text-[10px] uppercase tracking-widest text-gray-400">
        Viaseg · Assessoria em Medicina Ocupacional
      </p>
    </div>
  );
};

const AboutSlide = (props) => (
  <SlideShell {...props}>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6 flex-1 min-h-0 overflow-hidden">
      <div className="flex flex-col justify-center overflow-hidden">
        <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2 md:mb-3">Sobre a Avaliação</h2>
        <p className="text-[11px] sm:text-sm text-gray-700 leading-relaxed mb-1.5 md:mb-2">
          Foi aplicado o <strong>Copenhagen Psychosocial Questionnaire (COPSOQ II)</strong> —
          versão curta, instrumento psicométrico de referência mundial para
          avaliação de riscos psicossociais no trabalho.
        </p>
        <p className="text-[11px] sm:text-sm text-gray-700 leading-relaxed mb-1.5 md:mb-2">
          A coleta foi <strong>100% anônima e voluntária</strong>, em conformidade com a
          LGPD e os princípios éticos do Conselho Federal de Psicologia.
        </p>
        <p className="text-[11px] sm:text-sm text-gray-700 leading-relaxed">
          A classificação dos riscos segue os critérios de severidade da
          <strong> NR-01</strong>, transpondo as percepções dos colaboradores em
          indicadores estatísticos objetivos.
        </p>
      </div>
      <div className="flex flex-col justify-center overflow-hidden">
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-3 sm:p-5 border border-orange-200">
          <h3 className="text-xs sm:text-sm font-bold text-gray-800 uppercase tracking-wide mb-2 md:mb-3">
            Escala de Classificação (NR-01)
          </h3>
          <div className="space-y-1.5 md:space-y-2.5">
            {[
              { lvl: "Baixo", range: "1,0 – 2,0", color: "#10B981", desc: "Ambiente saudável; manter prevenção" },
              { lvl: "Moderado", range: "2,1 – 3,0", color: "#CA8A04", desc: "Atenção pontual; ação corretiva" },
              { lvl: "Alto", range: "3,1 – 4,0", color: "#F97316", desc: "Intervenção estruturada e PGR" },
              { lvl: "Crítico", range: "4,1 – 5,0", color: "#EF4444", desc: "Ação imediata; mitigação prioritária" },
            ].map((r) => (
              <div key={r.lvl} className="flex items-start gap-2 sm:gap-3">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1" style={{ backgroundColor: r.color }}></span>
                <div className="flex-1 grid grid-cols-3 gap-x-2 gap-y-0 items-center text-[10px] sm:text-xs">
                  <span className="font-bold text-gray-800">{r.lvl}</span>
                  <span className="text-gray-600 font-mono">{r.range}</span>
                  <span className="text-gray-500 truncate">{r.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </SlideShell>
);

const BigNumbersSlide = (props) => {
  const { stats, overallRisk, adesaoPercent, numSectors, publicMode } = props;
  return (
    <SlideShell {...props}>
      <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2 sm:mb-3 md:mb-4 flex-shrink-0">Indicadores Principais</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4 flex-1 min-h-0">
        <BigStat
          label="Total de Respondentes"
          value={stats?.total_assessments || 0}
          sub={`${stats?.numero_funcionarios || 0} colaboradores totais`}
          icon={<Users className="w-4 h-4 sm:w-5 sm:h-5" />}
          accent="#6366f1"
        />
        <BigStat
          label="Taxa de Adesão"
          value={adesaoPercent !== null ? `${adesaoPercent}%` : "—"}
          sub={adesaoPercent !== null && adesaoPercent >= 80 ? "Excelente representatividade" : adesaoPercent !== null && adesaoPercent >= 50 ? "Adesão satisfatória" : "Adesão a aprimorar"}
          icon={<CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />}
          accent="#10B981"
        />
        {publicMode ? (
          <BigStat
            label="Setores Avaliados"
            value={numSectors || 0}
            sub="Áreas avaliadas na empresa"
            icon={<Building2 className="w-4 h-4 sm:w-5 sm:h-5" />}
            accent="#6366f1"
          />
        ) : (
          <BigStat
            label="Pontuação Geral"
            value={`${(stats?.overall_score || 0).toFixed(2)}`}
            sub="Escala 1,00 → 5,00"
            icon={<BarChart3 className="w-4 h-4 sm:w-5 sm:h-5" />}
            accent={overallRisk.color}
          />
        )}
        <BigStat
          label="Nível Predominante"
          value={overallRisk.level.toUpperCase()}
          sub={publicMode ? `${numSectors} setor(es) avaliado(s)` : `${numSectors} setor(es) avaliado(s)`}
          icon={<AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5" />}
          accent={overallRisk.color}
          valueColor={overallRisk.color}
        />
      </div>
    </SlideShell>
  );
};

const BigStat = ({ label, value, sub, icon, accent, valueColor }) => {
  const isLongWord = typeof value === "string" && value.length > 5;
  const valueClass = isLongWord
    ? "text-xl sm:text-3xl font-bold mt-1 sm:mt-2 tracking-tight"
    : "text-2xl sm:text-4xl md:text-5xl font-bold mt-1 sm:mt-2";
  return (
    <div className="rounded-xl sm:rounded-2xl border-2 p-2.5 sm:p-4 md:p-5 flex flex-col justify-between min-h-0 overflow-hidden" style={{ borderColor: accent + "33" }}>
      <div className="flex items-start gap-1.5 sm:gap-2 text-[9px] sm:text-[11px] font-semibold uppercase tracking-wide leading-snug" style={{ color: accent }}>
        <span className="flex-shrink-0 mt-0.5">{icon}</span>
        <span>{label}</span>
      </div>
      <div className={valueClass} style={{ color: valueColor || "#111827", wordBreak: "break-word" }}>
        {value}
      </div>
      <p className="text-[9px] sm:text-xs text-gray-500 mt-1 leading-snug">{sub}</p>
    </div>
  );
};

const RadarSlide = (props) => {
  const { chartData, publicMode } = props;
  const radarData = chartData.map((d) => ({ dimension: d.name, value: d.effective, level: d.level }));
  return (
    <SlideShell {...props}>
      <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1 sm:mb-2 flex-shrink-0">Panorama Geral</h2>
      <p className="text-[11px] sm:text-sm text-gray-500 mb-2 md:mb-3 flex-shrink-0">
        {publicMode
          ? "Perfil qualitativo de risco das dimensões avaliadas. Escalas positivas (Relações, Autonomia e Sentido) são consideradas invertidas para comparação."
          : "Pontuação efetiva de risco das 5 dimensões. Escalas positivas (Relações, Autonomia e Sentido) são automaticamente invertidas."}
      </p>
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarData}>
            <PolarGrid gridType="polygon" stroke="#d1d5db" />
            <PolarAngleAxis dataKey="dimension" tick={{ fill: "#374151", fontSize: 9, fontWeight: 600 }} />
            <Radar name="Risco" dataKey="value" stroke="#f97316" fill="#f97316" fillOpacity={0.35} strokeWidth={2} />
            {publicMode ? (
              <Tooltip formatter={(_v, _n, item) => [item?.payload?.level || "—", "Nível"]} />
            ) : (
              <Tooltip formatter={(v) => `${v.toFixed(2)} / 5,00`} />
            )}
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </SlideShell>
  );
};

const DistributionSlide = (props) => {
  const { gravityDistribution } = props;
  return (
    <SlideShell {...props}>
      <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1 sm:mb-2 flex-shrink-0">Distribuição de Riscos</h2>
      <p className="text-[11px] sm:text-sm text-gray-500 mb-2 md:mb-3 flex-shrink-0">
        Quantas dimensões caem em cada nível de gravidade (4 níveis NR-01).
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4 flex-1 min-h-0">
        <div className="min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={gravityDistribution}
                cx="50%"
                cy="50%"
                outerRadius="70%"
                dataKey="value"
                label={({ name, value, percent }) =>
                  `${name}: ${value} (${(percent * 100).toFixed(0)}%)`
                }
                labelLine={false}
              >
                {gravityDistribution.map((entry, idx) => (
                  <Cell key={idx} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="flex flex-col justify-center gap-1.5 sm:gap-2 overflow-hidden">
          {gravityDistribution.map((d) => (
            <div key={d.name} className="flex items-center gap-2 sm:gap-3 p-1.5 sm:p-2.5 rounded-lg border border-gray-200">
              <span className="w-3 h-3 sm:w-4 sm:h-4 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }}></span>
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm font-bold text-gray-800">{d.name}</p>
                <p className="text-[9px] sm:text-[11px] text-gray-500">
                  {d.value} dimens{d.value === 1 ? "ão" : "ões"} nesta faixa
                </p>
              </div>
              <span className="text-xl sm:text-2xl font-bold" style={{ color: d.color }}>
                {d.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </SlideShell>
  );
};

const DimensionsSlide = (props) => {
  const { chartData, publicMode } = props;
  const bars = [...chartData].sort((a, b) => b.effective - a.effective);
  return (
    <SlideShell {...props}>
      <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1 sm:mb-2 flex-shrink-0">Análise por Dimensão</h2>
      <p className="text-[11px] sm:text-sm text-gray-500 mb-2 md:mb-3 flex-shrink-0">
        {publicMode
          ? "Nível qualitativo de risco por dimensão avaliada (Baixo → Crítico)."
          : "Pontuação efetiva de risco (1,00 = baixo · 5,00 = crítico) por dimensão avaliada."}
      </p>
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={bars} layout="vertical" margin={{ left: 20, right: 30 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
            <XAxis
              type="number"
              domain={[0, 5]}
              tick={publicMode ? false : { fontSize: 11 }}
              axisLine={!publicMode}
              tickLine={!publicMode}
            />
            <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 10, fontWeight: 600 }} interval={0} />
            <Tooltip
              formatter={(v, _n, item) =>
                publicMode
                  ? [item?.payload?.level || "—", "Nível"]
                  : [`${v.toFixed(2)} / 5,00`, "Risco efetivo"]
              }
              labelFormatter={(l) => `Dimensão: ${l}`}
            />
            <Bar dataKey="effective" radius={[0, 6, 6, 0]}>
              {bars.map((b, i) => (
                <Cell key={i} fill={b.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </SlideShell>
  );
};

const SectorsSlide = (props) => {
  const { sectorComparison, publicMode } = props;
  const top = sectorComparison.slice(0, 8);
  const data = top.map((s) => ({
    ...s,
    overall_risk: publicMode ? (LEVEL_TO_CHART_VALUE[s.risk_level] || 2.5) : s.overall_risk,
  }));
  return (
    <SlideShell {...props} accent="purple">
      <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1 sm:mb-2 flex-shrink-0">Comparativo Setorial</h2>
      <p className="text-[11px] sm:text-sm text-gray-500 mb-2 md:mb-3 flex-shrink-0">
        {publicMode
          ? "Nível de risco por setor avaliado, ordenado do mais crítico para o menos."
          : "Pontuação geral de risco por setor avaliado, ordenado do maior para o menor."}
      </p>
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ left: 20, right: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
            <XAxis
              type="number"
              domain={[0, 5]}
              tick={publicMode ? false : { fontSize: 11 }}
              axisLine={!publicMode}
              tickLine={!publicMode}
            />
            <YAxis type="category" dataKey="setor" width={110} tick={{ fontSize: 10, fontWeight: 600 }} interval={0} />
            <Tooltip
              formatter={(v, _n, item) =>
                publicMode
                  ? [item?.payload?.risk_level || "—", "Nível"]
                  : [`${v.toFixed(2)} / 5,00`, "Risco"]
              }
              labelFormatter={(l) => `Setor: ${l}`}
            />
            <Bar dataKey="overall_risk" radius={[0, 6, 6, 0]}>
              {data.map((s, i) => (
                <Cell key={i} fill={s.risk_color || "#6366f1"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </SlideShell>
  );
};

const CriticalSlide = (props) => {
  const { topCritical, aiAnalysis, publicMode } = props;
  const priorities = aiAnalysis?.priority_actions || [];
  return (
    <SlideShell {...props} accent="purple">
      <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1 sm:mb-2 flex-shrink-0">Pontos Críticos & Prioridades</h2>
      <p className="text-[11px] sm:text-sm text-gray-500 mb-2 md:mb-3 flex-shrink-0">
        As 3 dimensões mais críticas e as ações prioritárias recomendadas.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4 flex-1 min-h-0 overflow-hidden">
        {/* Top 3 critical */}
        <div className="space-y-1.5 sm:space-y-2 min-h-0 overflow-hidden flex flex-col">
          <h3 className="text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wide flex-shrink-0">Top 3 Dimensões Críticas</h3>
          {topCritical.map((d, i) => (
            <div key={d.name} className="p-2 sm:p-3 rounded-xl border-l-4 bg-gray-50 flex-1 min-h-0" style={{ borderColor: d.color }}>
              <div className="flex justify-between items-start">
                <span className="text-lg sm:text-2xl font-bold text-gray-300 leading-none">#{i + 1}</span>
                <span
                  className="px-1.5 py-0.5 rounded text-white text-[9px] sm:text-[10px] font-bold uppercase"
                  style={{ backgroundColor: d.color }}
                >
                  {d.level}
                </span>
              </div>
              <p className="text-[11px] sm:text-sm font-bold text-gray-800 mt-0.5 leading-tight">{d.name}</p>
              <p className="text-[9px] sm:text-xs text-gray-500 leading-tight">
                {publicMode ? (
                  <>
                    Nível de risco: <strong style={{ color: d.color }}>{d.level}</strong>
                  </>
                ) : (
                  <>
                    Pontuação: <strong style={{ color: d.color }}>{d.effective.toFixed(2)}</strong> / 5,00
                  </>
                )}
                {d.positive && <span className="italic"> · invertida</span>}
              </p>
            </div>
          ))}
        </div>

        {/* Priority actions */}
        <div className="min-h-0 overflow-hidden flex flex-col">
          <h3 className="text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 sm:mb-2 flex-shrink-0">Ações Prioritárias</h3>
          {priorities.length > 0 ? (
            <ul className="space-y-1 sm:space-y-1.5 flex-1 min-h-0 overflow-hidden">
              {priorities.slice(0, 4).map((action, idx) => (
                <li key={idx} className="flex gap-2 p-1.5 sm:p-2 bg-purple-50 rounded-lg border border-purple-100">
                  <span className="flex-shrink-0 w-4 h-4 sm:w-5 sm:h-5 bg-purple-500 text-white text-[9px] sm:text-[10px] font-bold rounded-full flex items-center justify-center">
                    {idx + 1}
                  </span>
                  <p className="text-[10px] sm:text-xs text-gray-700 leading-snug">{action}</p>
                </li>
              ))}
            </ul>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 sm:p-4 text-center">
              <Sparkles className="w-6 h-6 sm:w-8 sm:h-8 text-amber-500 mx-auto mb-1.5" />
              <p className="text-[11px] sm:text-sm text-gray-700 font-medium mb-1">
                Análise de IA não disponível
              </p>
              <p className="text-[9px] sm:text-xs text-gray-500">
                {publicMode
                  ? "A análise interpretativa ainda não foi gerada pela equipe técnica."
                  : 'Volte ao Dashboard e clique em "Gerar Análise" para obter recomendações.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </SlideShell>
  );
};

const ConclusionSlide = (props) => {
  const { aiAnalysis, overallRisk, chartData = [] } = props;
  // Extract the top "headline" insight: number of dimensions in Alto/Critico.
  const highCritical = (chartData || []).filter((d) => d.effective >= 3.05);
  const isHealthy = highCritical.length === 0;
  const headline = isHealthy
    ? "Ambiente psicossocial saudável."
    : `${highCritical.length} ${highCritical.length === 1 ? "dimensão" : "dimensões"} exigem ação prioritária.`;
  const subHeadline = isHealthy
    ? "Manter as boas práticas e reavaliar periodicamente."
    : "Integrar essas dimensões ao GRO/PGR conforme exige a NR-01.";

  // Split conclusion into bite-sized paragraphs for big-text presentation.
  const conclusion = aiAnalysis?.general_recommendations || "";
  // Pegamos no máximo as 3 primeiras frases para garantir legibilidade.
  const sentences = conclusion
    .split(/(?<=[\.\!\?])\s+(?=[A-ZÁÉÍÓÚÂÊÔÃÕÇ])/g)
    .filter((s) => s.trim().length > 0)
    .slice(0, 3);

  return (
    <SlideShell {...props}>
      <h2 className="text-xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 flex-shrink-0">
        Conclusão
      </h2>

      {aiAnalysis?.general_recommendations ? (
        <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-12 gap-3 sm:gap-5 overflow-hidden">
          {/* Headline / Veredito */}
          <div
            className="md:col-span-5 rounded-2xl p-4 sm:p-6 flex flex-col justify-center text-white relative overflow-hidden"
            style={{ background: `linear-gradient(135deg, ${overallRisk.color} 0%, ${overallRisk.color}cc 100%)` }}
          >
            <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] opacity-80 mb-2">
              Quadro Geral
            </div>
            <div className="text-2xl sm:text-4xl md:text-5xl font-bold leading-tight mb-2 sm:mb-3">
              {overallRisk.level}
            </div>
            <div className="text-sm sm:text-lg md:text-xl font-semibold leading-snug mb-2 sm:mb-3">
              {headline}
            </div>
            <div className="text-xs sm:text-sm md:text-base opacity-90 leading-snug">
              {subHeadline}
            </div>
          </div>

          {/* Mensagem-chave em texto grande */}
          <div className="md:col-span-7 rounded-2xl p-4 sm:p-6 border border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50 flex flex-col justify-center overflow-hidden">
            <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-orange-700 font-bold mb-2 sm:mb-3">
              Mensagem-chave
            </div>
            <div className="space-y-2 sm:space-y-3 overflow-hidden">
              {sentences.map((s, idx) => (
                <p
                  key={idx}
                  className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-800 leading-relaxed font-medium"
                >
                  {idx === 0 && <span className="text-orange-500 mr-1">•</span>}
                  {idx === 1 && <span className="text-orange-500 mr-1">•</span>}
                  {idx === 2 && <span className="text-orange-500 mr-1">•</span>}
                  {s.trim()}
                </p>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-gray-50 rounded-2xl p-4 sm:p-8 border-2 border-dashed border-gray-300 flex-1 min-h-0 flex flex-col items-center justify-center text-center">
          <Sparkles className="w-10 h-10 sm:w-14 sm:h-14 text-gray-400 mb-3" />
          <p className="text-base sm:text-xl text-gray-600 mb-2 font-medium">
            Análise interpretativa não gerada ainda.
          </p>
          <p className="text-sm sm:text-base text-gray-500 max-w-md">
            Considerando o cenário apurado com risco{" "}
            <strong style={{ color: overallRisk.color }}>{overallRisk.level}</strong>,
            recomenda-se integrar os resultados ao PGR e monitorar periodicamente as
            dimensões avaliadas.
          </p>
        </div>
      )}
    </SlideShell>
  );
};

const ClosingSlide = ({ empresa }) => (
  <div
    className="w-full h-full flex flex-col items-center justify-center p-6 sm:p-10 md:p-12 text-center"
    style={{
      background: "linear-gradient(135deg, #1f2937 0%, #111827 100%)",
      color: "white",
    }}
  >
    <Shield className="w-12 h-12 sm:w-16 sm:h-16 text-orange-400 mb-4 sm:mb-6" />
    <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">Obrigado</h1>
    <p className="text-sm sm:text-base md:text-xl text-gray-300 max-w-2xl mb-6 sm:mb-10 px-2">
      Concluímos a apresentação dos resultados da Avaliação de Riscos
      Psicossociais de <strong>{empresa}</strong>.
    </p>

    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 max-w-3xl w-full">
      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-5 border border-white/10">
        <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-orange-400 mx-auto mb-1 sm:mb-2" />
        <p className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wide">Próximo Passo</p>
        <p className="text-xs sm:text-sm font-bold mt-1">Integrar ao PGR</p>
      </div>
      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-5 border border-white/10">
        <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-orange-400 mx-auto mb-1 sm:mb-2" />
        <p className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wide">Re-avaliação</p>
        <p className="text-xs sm:text-sm font-bold mt-1">Em 12 meses</p>
      </div>
      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-5 border border-white/10">
        <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-orange-400 mx-auto mb-1 sm:mb-2" />
        <p className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wide">Acompanhamento</p>
        <p className="text-xs sm:text-sm font-bold mt-1">Plano de Ação</p>
      </div>
    </div>

    <p className="text-[10px] sm:text-xs uppercase tracking-[0.25em] sm:tracking-[0.3em] text-gray-500 mt-8 sm:mt-12">
      Viaseg · Assessoria em Medicina Ocupacional
    </p>
  </div>
);

export default Presentation;
